/**
 * Centre stage for the globe modes: the Cesium globe plus a thin HUD
 * (network, timeframe, scene and style, analysis state, tracking, sun outage,
 * planet navigation). Everything heavier lives in the panels.
 *
 * Layout (shell/layout.ts, hudLayout): on stages from 1100 px every scene
 * action sits in the top-right group; narrower, the secondary ones (map style,
 * catalogue, reset view, deep-space navigation) move into the More menu, and
 * when even that row would reach the top-left pills the group stacks
 * vertically along the right edge. The live-state stack (analysis, sun
 * outage, following, contacts) sits bottom left, lifted above the tablet
 * detail sheet by --sheet-h.
 */
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { DropdownMenu } from 'radix-ui';
import {
  AlertTriangle,
  Check,
  Crosshair,
  Ellipsis,
  Library,
  Loader2,
  LocateFixed,
  Map,
  Moon,
  Orbit,
  Play,
  RotateCcw,
  SatelliteDish,
  Sun,
  X,
} from 'lucide-react';
import { useUI, clockNow } from '@/state/store';
import { Chip, IconButton, TextButton } from '@/components/ui/primitives';
import { cn } from '@/lib/cn';
import { hudLayout } from '@/features/shell/layout';
import { useElementSize, useMeasure } from '@/features/shell/useViewport';
import { TIMEFRAME, WEATHER } from '../constants';
import { toggleGuamDemo } from './guamDemo';
import { openCatalog } from '../catalog/useCatalog';
import { exportCsvQuick } from '../export/csv';
import { ExportMenu } from './ExportMenu';
import { LoaderGlobe } from '../globe/LoaderGlobe';
import { getGlobeEngine } from '../globe/engineHandle';
import { PlaceButton } from '../place/PlaceButton';
import { useModeContext } from './modeContext';
import { PlacementLayer } from '../place/PlacementLayer';
import { DiveButton } from '@/features/dive/DiveButton';
import { isDeepSpaceNetwork, selectMissionById, useLoader } from '../store';
import type { MapStyle } from '../globe/GlobeEngine';
import { MAP_STYLES } from '../globe/mapStyles';
import { WeatherMenu } from './WeatherMenu';
import type { NetworkId } from '../types';
import { pct } from './format';
import { usePresentation } from './presentation';
import { PlanetPanel } from './panels/PlanetPanel';
import { MissionReplayBar } from './MissionReplayBar';
import { SelectedTwinAction } from './SelectedTwinAction';
import { CatalogueHud, DebrisToggle } from '../ssa/Hud';
import { CatalogueDisplayControls, CatalogueLabelsToggle } from '../ssa/DisplayControls';
import { SsaRuntime } from '../ssa/Runtime';

import { TimeConductor } from '@/features/shell/TimeConductor';

const pill =
  'pointer-events-auto flex items-center gap-1 rounded-full border border-hairline bg-raised/85 backdrop-blur';
const menuClass =
  'z-50 min-w-[200px] rounded-panel border border-hairline-strong bg-raised p-1 shadow-elev-2';
const menuItemClass =
  'flex cursor-pointer select-none items-center gap-2 rounded-control px-2.5 py-1.5 text-xs text-ink outline-none data-[disabled]:cursor-not-allowed data-[disabled]:opacity-40 data-[highlighted]:bg-overlay';
const menuLabelClass = 'px-2.5 pb-1 pt-1.5 text-[10px] uppercase tracking-wider text-ink-3';

/** The width a group takes laid out in a row: its children side by side plus gaps, padding and border. */
function rowWidth(group: HTMLElement): number {
  const style = getComputedStyle(group);
  const kids = [...group.children] as HTMLElement[];
  const gap = parseFloat(style.columnGap) || 0;
  const frame =
    parseFloat(style.paddingLeft) +
    parseFloat(style.paddingRight) +
    parseFloat(style.borderLeftWidth) +
    parseFloat(style.borderRightWidth);
  return Math.ceil(
    kids.reduce((sum, k) => sum + k.getBoundingClientRect().width, 0) +
      gap * Math.max(0, kids.length - 1) +
      frame,
  );
}
const sameNumber = (a: number, b: number) => a === b;

function hoursLabel(h: number): string {
  if (h < 24) return `${h} h`;
  if (h < 168) return `${h / 24} d`;
  if (h < 720) return `${h / 168} w`;
  if (h < 8760) return `${Math.round(h / 730)} mo`;
  return '1 y';
}

function MapStyleItems() {
  const mapStyle = useLoader((s) => s.mapStyle);
  const setView = useLoader((s) => s.setView);
  const dayNight = useLoader((s) => s.dayNight);
  const nightLights = useLoader((s) => s.nightLights);
  return (
    <>
      <DropdownMenu.RadioGroup
        value={mapStyle}
        onValueChange={(v) => setView({ mapStyle: v as MapStyle, dayNight: false, nightLights: false })}
      >
        {MAP_STYLES.map((s) => (
          <DropdownMenu.RadioItem key={s.id} value={s.id} className={menuItemClass}>
            <span className="flex w-3.5 justify-center">
              <DropdownMenu.ItemIndicator>
                <Check aria-hidden className="h-3.5 w-3.5 text-accent" />
              </DropdownMenu.ItemIndicator>
            </span>
            {s.label}
          </DropdownMenu.RadioItem>
        ))}
      </DropdownMenu.RadioGroup>
      <DropdownMenu.Separator className="my-1 h-px bg-hairline" />
      <DropdownMenu.Label className={menuLabelClass}>Globe lighting</DropdownMenu.Label>
      <DropdownMenu.CheckboxItem
        checked={dayNight}
        onCheckedChange={(v) => setView({ dayNight: v, ...(!v ? { nightLights: false } : {}) })}
        className={menuItemClass}
        onSelect={(e) => e.preventDefault()}
      >
        <span className="w-3.5">
          <DropdownMenu.ItemIndicator>
            <Check className="h-3.5 w-3.5 text-accent" />
          </DropdownMenu.ItemIndicator>
        </span>{' '}
        Day / night shading
      </DropdownMenu.CheckboxItem>
      <DropdownMenu.CheckboxItem
        checked={nightLights && dayNight}
        onCheckedChange={(v) => setView({ nightLights: v, ...(v ? { dayNight: true } : {}) })}
        className={menuItemClass}
        onSelect={(e) => e.preventDefault()}
      >
        <span className="w-3.5">
          <DropdownMenu.ItemIndicator>
            <Check className="h-3.5 w-3.5 text-accent" />
          </DropdownMenu.ItemIndicator>
        </span>{' '}
        City lights at night
      </DropdownMenu.CheckboxItem>
    </>
  );
}

/** Deep-space navigation for the artemis and mars networks. */
function deepSpaceActions(
  network: NetworkId,
  openPlanet: (body: 'moon' | 'mars') => void,
): { key: string; label: string; icon: typeof Orbit; run: () => void }[] {
  const out: { key: string; label: string; icon: typeof Orbit; run: () => void }[] =
    network === 'artemis'
      ? [
          {
            key: 'overview',
            label: 'Earth and Moon overview',
            icon: Orbit,
            run: () => getGlobeEngine()?.flyToMissionOverview(),
          },
        ]
      : [];
  if (network === 'artemis')
    out.push({
      key: 'moon',
      label: 'Moon surface',
      icon: Moon,
      run: () => openPlanet('moon'),
    });
  if (network === 'mars')
    out.push({
      key: 'mars',
      label: 'Mars surface',
      icon: Sun,
      run: () => openPlanet('mars'),
    });
  return out;
}

/** The analysis state in words, for the polite live region (one message per state, not per progress tick). */
function analysisAnnouncement(
  status: 'idle' | 'running' | 'done' | 'error',
  error: string | null,
  summary: { missionCount: number; overallSLA: number } | null,
): string {
  if (status === 'running') return 'Analysis running.';
  if (status === 'error') return `Analysis failed: ${error ?? 'unknown error'}.`;
  if (status === 'done' && summary)
    return `Analysis complete: ${summary.missionCount} missions, overall SLA ${pct(summary.overallSLA)}.`;
  return '';
}

export function LoaderStage({
  parked = false,
  mode = 'loader',
}: {
  parked?: boolean;
  mode?: import('@/state/store').Mode;
}) {
  const network = useLoader((s) => s.network);
  const [planet, setPlanet] = useState<{ body: 'moon' | 'mars'; network: NetworkId } | null>(null);
  const visiblePlanet = !parked && planet?.network === network ? planet.body : null;
  const openPlanet = (body: 'moon' | 'mars') => setPlanet({ body, network });
  const contactStyle = usePresentation((s) => s.contactStyle);
  useModeContext(mode, network, parked);
  const datasetStatus = useLoader((s) => s.datasetStatus);
  const dataset = useLoader((s) => s.dataset);
  const activeMissionCount = useLoader((s) => s.activeMissionIds.length);
  const timeframe = useLoader((s) => s.timeframe);
  const setTimeframeHours = useLoader((s) => s.setTimeframeHours);
  const setFiscalYear = useLoader((s) => s.setFiscalYear);
  const sceneMode = useLoader((s) => s.sceneMode);
  const setView = useLoader((s) => s.setView);
  const coverageEnabled = useLoader((s) => s.coverageEnabled);
  const analysisStatus = useLoader((s) => s.analysisStatus);
  const progress = useLoader((s) => s.analysisProgress);
  const analysisError = useLoader((s) => s.analysisError);
  const summary = useLoader((s) => s.results?.summary ?? null);
  const requestAnalysis = useLoader((s) => s.requestAnalysis);
  const tracked = useLoader((s) => s.trackedMissionId);
  const setTracked = useLoader((s) => s.setTracked);
  const trackedMission = useLoader((s) => selectMissionById(s, s.trackedMissionId));
  const sunOutage = useLoader((s) => s.sunOutage);
  const inContact = useLoader((s) => s.inContact);
  const presenter = useUI((s) => s.presenter);
  const profile = useLoader((s) => s.profile);
  const setProfile = useLoader((s) => s.setProfile);
  const [searchParams] = useSearchParams();
  const [root, setRoot] = useState<HTMLDivElement | null>(null);
  const [primaryRow, setPrimaryRow] = useState<HTMLDivElement | null>(null);
  const [sceneGroup, setSceneGroup] = useState<HTMLDivElement | null>(null);
  const stage = useElementSize(root);
  const primary = useElementSize(primaryRow);
  const sceneRow = useMeasure(sceneGroup, rowWidth, 0, sameNumber);
  const layout = hudLayout(stage.width, primary.width, sceneRow);

  // /loader?profile=noaa-core is the slim NOAA-only instance.
  useEffect(() => {
    const p = searchParams.get('profile');
    if (p === 'noaa-core' && profile !== 'noaa-core') setProfile('noaa-core');
    else if (p === 'full' && profile !== 'full') setProfile('full');
  }, [searchParams, profile, setProfile]);

  useEffect(() => {
    if (parked) return;
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (
        t &&
        (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)
      )
        return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key === 'r' || e.key === 'R') {
        requestAnalysis();
        e.preventDefault();
      } else if (e.key === 'g' || e.key === 'G') {
        toggleGuamDemo();
        e.preventDefault();
      } else if (e.key === 'c' || e.key === 'C') {
        openCatalog();
        e.preventDefault();
      } else if (e.key === 'e' || e.key === 'E') {
        exportCsvQuick();
        e.preventDefault();
      } else if ((e.key === 'w' || e.key === 'W') && useLoader.getState().network !== 'full_catalogue') {
        const keys = [null, ...Object.keys(WEATHER.LAYERS)];
        const st = useLoader.getState();
        const next = keys[(keys.indexOf(st.weatherLayer) + 1) % keys.length];
        st.setView({ weatherLayer: next });
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [requestAnalysis, parked]);

  const deepSpace = isDeepSpaceNetwork(network);
  const deepActions = deepSpace ? deepSpaceActions(network, openPlanet) : [];

  const sceneToggle = (
    <>
      <TextButton
        active={sceneMode === '2D'}
        aria-pressed={sceneMode === '2D'}
        onClick={() => setView({ sceneMode: '2D' })}
        title="Flat map"
      >
        2D
      </TextButton>
      <TextButton
        active={sceneMode === '3D'}
        aria-pressed={sceneMode === '3D'}
        onClick={() => setView({ sceneMode: '3D' })}
        title="Globe"
      >
        3D
      </TextButton>
    </>
  );

  return (
    <div
      ref={setRoot}
      className="relative h-full w-full"
      data-hud-layout={layout.overflow ? (layout.vertical ? 'vertical' : 'compact') : 'full'}
    >
      <LoaderGlobe parked={parked || Boolean(visiblePlanet)} />
      {network === 'full_catalogue' && <SsaRuntime parked={parked} />}
      {network === 'full_catalogue' && !parked && !presenter && <CatalogueHud />}
      {!parked && !visiblePlanet && <SelectedTwinAction />}
      {visiblePlanet && (
        <div className="absolute inset-0 z-20">
          <PlanetPanel key={visiblePlanet} body={visiblePlanet} onClose={() => setPlanet(null)} />
        </div>
      )}
      {!presenter && network !== 'full_catalogue' && (
        <div
          className="pointer-events-none absolute bottom-[calc(var(--sheet-h,0px)+0.75rem)] right-3 rounded-control border border-hairline bg-raised/90 px-3 py-2 text-right text-xs backdrop-blur"
          data-testid="mode-purpose"
        >
          <div className="font-medium text-ink">
            {mode === 'operations'
              ? 'Operations · current activity'
              : mode === 'analysis'
                ? 'Analysis · coverage and capacity'
                : 'Loader · predicted contacts'}
          </div>
          <div className="mt-0.5 text-[10px] text-ink-3">
            {mode === 'operations'
              ? 'Calculated contacts and upcoming passes'
              : mode === 'analysis'
                ? 'Allocation, utilisation and what-if scenarios'
                : contactStyle === 'schematic'
                  ? 'Curved links are schematic · motion is illustrative'
                  : 'Straight links show line of sight · receiving is unconfirmed'}
          </div>
          <div className="mt-1 flex justify-end gap-3 text-[10px] text-ink-2" aria-label="Map legend">
            <span>● Spacecraft</span>
            <span>○ Ground site</span>
            <span>━ Predicted link</span>
          </div>
          {dataset?.source === 'live' && dataset.missions.length > activeMissionCount && (
            <div className="mt-1 text-[10px] text-ink-3">
              Dim spacecraft: outside the {activeMissionCount}-mission planning selection
            </div>
          )}
        </div>
      )}

      {/* Polite live region: the analysis state as it changes (always in the DOM so it is announced). */}
      <div className="sr-only" role="status" aria-live="polite" data-testid="analysis-status">
        {analysisAnnouncement(analysisStatus, analysisError, summary)}
      </div>

      {/* Top-left: timeframe and antenna coverage, then Place on its own row */}
      {!presenter && (
        <div
          data-hud="primary"
          className={cn(
            'pointer-events-none absolute left-3 flex flex-col items-start gap-2',
            stage.width < 720 ? 'top-14' : 'top-3',
            layout.vertical ? 'max-w-[calc(100%-7rem)]' : 'max-w-[calc(100%-1.5rem)]',
          )}
        >
          <div ref={setPrimaryRow} className="flex w-max max-w-full flex-wrap items-center gap-2">
            {network === 'full_catalogue' && <DebrisToggle />}
            {network === 'full_catalogue' && <CatalogueDisplayControls />}
            {network === 'full_catalogue' && <CatalogueLabelsToggle />}
            {network === 'rfims' && (
              <div className={cn(pill, 'max-w-sm px-3 py-2 text-xs text-ink-2')}>
                Four client-positioned monitoring sites. RF coverage and mission assignments are unverified.{' '}
                <Link className="text-accent underline" to="/maintenance">
                  Open Maintenance
                </Link>
              </div>
            )}

            {network !== 'artemis' && network !== 'rfims' && network !== 'full_catalogue' && (
              <div className={cn(pill, 'px-1.5 py-1')}>
                <span aria-hidden className="ml-1.5 text-[10px] uppercase tracking-wider text-ink-3">
                  Window
                </span>
                <select
                  aria-label="Analysis timeframe"
                  value={timeframe.hours}
                  onChange={(e) => setTimeframeHours(Number(e.target.value), clockNow())}
                  className="rounded-control bg-transparent px-1.5 py-0.5 text-xs text-ink focus:bg-inset"
                >
                  {TIMEFRAME.OPTIONS_HOURS.map((h) => (
                    <option key={h} value={h}>
                      {hoursLabel(h)}
                    </option>
                  ))}
                </select>
                {network !== 'noaa' && (
                  <select
                    aria-label="Fiscal year"
                    value={timeframe.fiscalYear}
                    onChange={(e) => setFiscalYear(Number(e.target.value))}
                    className="rounded-control bg-transparent px-1.5 py-0.5 text-xs text-ink focus:bg-inset"
                  >
                    {TIMEFRAME.FISCAL_YEARS.map((fy) => (
                      <option key={fy} value={fy}>
                        FY{fy}
                      </option>
                    ))}
                  </select>
                )}
                <TextButton
                  onClick={requestAnalysis}
                  disabled={analysisStatus === 'running' || datasetStatus !== 'ready'}
                  title="Run the analysis (R)"
                  className="flex items-center gap-1"
                >
                  {analysisStatus === 'running' ? (
                    <Loader2 aria-hidden className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Play aria-hidden className="h-3.5 w-3.5" />
                  )}
                  Run
                </TextButton>
              </div>
            )}
            <button
              type="button"
              role="switch"
              aria-label="Coverage circles"
              aria-checked={coverageEnabled}
              title={`${coverageEnabled ? 'Hide' : 'Show'} antenna coverage`}
              onClick={() => setView({ coverageEnabled: !coverageEnabled })}
              className={cn(
                pill,
                'h-8 shrink-0 gap-2 px-2.5 transition-colors hover:bg-overlay',
                coverageEnabled ? 'text-accent' : 'text-ink-3',
              )}
            >
              <SatelliteDish aria-hidden className="h-4 w-4" />
              <span
                aria-hidden
                className={cn(
                  'relative h-4 w-7 rounded-full border transition-colors',
                  coverageEnabled ? 'border-accent bg-accent/80' : 'border-hairline-strong bg-inset',
                )}
              >
                <span
                  className={cn(
                    'absolute top-0.5 h-2.5 w-2.5 rounded-full transition-[left]',
                    coverageEnabled ? 'left-3.5 bg-accent-ink' : 'left-0.5 bg-ink-3',
                  )}
                />
              </span>
            </button>
          </div>

          <div className="flex gap-2 whitespace-nowrap">
            <PlaceButton />
            {deepSpace && (
              <div className={cn(pill, 'px-2 py-1')}>
                <TextButton onClick={() => openPlanet(network === 'artemis' ? 'moon' : 'mars')}>
                  {network === 'artemis' ? 'Explore Moon' : 'Explore Mars'}
                </TextButton>
                {network === 'artemis' && (
                  <TextButton
                    onClick={() => {
                      setView({ sceneMode: '3D' });
                      getGlobeEngine()?.flyToMissionOverview();
                    }}
                  >
                    Earth–Moon overview
                  </TextButton>
                )}
              </div>
            )}
          </div>
          {network === 'artemis' && <MissionReplayBar />}
        </div>
      )}

      {!parked && !presenter && (
        <div data-hud="time" className="absolute right-3 top-3 max-w-[calc(100%-1.5rem)]">
          <TimeConductor compact={stage.width < 1050} wide={stage.width >= 1300} />
        </div>
      )}

      {/* Top-right: scene controls (inline from 1100 px, the secondary ones in More below it) */}
      {!presenter && (
        <div
          ref={setSceneGroup}
          data-hud="scene"
          role="group"
          aria-label="Scene controls"
          className={cn(
            'absolute right-3 top-14 flex items-center gap-1 border border-hairline bg-raised/85 p-1 backdrop-blur',
            layout.vertical ? 'flex-col rounded-panel' : 'rounded-full',
          )}
        >
          {sceneToggle}
          <WeatherMenu />
          {!layout.overflow && (
            <>
              <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                  <IconButton label="Map style">
                    <Map className="h-4 w-4" />
                  </IconButton>
                </DropdownMenu.Trigger>
                <DropdownMenu.Portal>
                  <DropdownMenu.Content align="end" sideOffset={6} collisionPadding={8} className={menuClass}>
                    <DropdownMenu.Label className={menuLabelClass}>Map style</DropdownMenu.Label>
                    <MapStyleItems />
                  </DropdownMenu.Content>
                </DropdownMenu.Portal>
              </DropdownMenu.Root>
              <IconButton label="Satellite catalogue (C)" onClick={openCatalog}>
                <Library className="h-4 w-4" />
              </IconButton>
            </>
          )}
          <ExportMenu />
          {!layout.overflow && (
            <IconButton label="Reset view" onClick={() => getGlobeEngine()?.resetView()}>
              <RotateCcw className="h-4 w-4" />
            </IconButton>
          )}
          <DiveButton />
          {!layout.overflow && deepActions.length > 0 && (
            <>
              <span aria-hidden className="mx-1 h-4 w-px bg-hairline-strong" />
              {deepActions.map((a) => {
                const Icon = a.icon;
                return (
                  <IconButton key={a.key} label={a.label} onClick={a.run}>
                    <Icon className="h-4 w-4" />
                  </IconButton>
                );
              })}
            </>
          )}
          {layout.overflow && (
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <IconButton label="More scene controls">
                  <Ellipsis className="h-4 w-4" />
                </IconButton>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  align="end"
                  side={layout.vertical ? 'left' : 'bottom'}
                  sideOffset={6}
                  collisionPadding={8}
                  className={menuClass}
                  data-testid="hud-more-menu"
                >
                  <DropdownMenu.Label className={menuLabelClass}>Map style</DropdownMenu.Label>
                  <MapStyleItems />
                  <DropdownMenu.Separator className="my-1 h-px bg-hairline" />
                  <DropdownMenu.Item className={menuItemClass} onSelect={() => openCatalog()}>
                    <Library aria-hidden className="h-3.5 w-3.5 text-ink-2" />
                    Satellite catalogue (C)
                  </DropdownMenu.Item>
                  <DropdownMenu.Item className={menuItemClass} onSelect={() => getGlobeEngine()?.resetView()}>
                    <RotateCcw aria-hidden className="h-3.5 w-3.5 text-ink-2" />
                    Reset view
                  </DropdownMenu.Item>
                  {deepActions.length > 0 && <DropdownMenu.Separator className="my-1 h-px bg-hairline" />}
                  {deepActions.map((a) => {
                    const Icon = a.icon;
                    return (
                      <DropdownMenu.Item key={a.key} className={menuItemClass} onSelect={a.run}>
                        <Icon aria-hidden className="h-3.5 w-3.5 text-ink-2" />
                        {a.label}
                      </DropdownMenu.Item>
                    );
                  })}
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          )}
        </div>
      )}

      {/* Bottom-left: live state, above the tablet detail sheet */}
      <div
        data-hud="status"
        className="pointer-events-none absolute bottom-[calc(var(--sheet-h,0px)+0.75rem)] left-3 flex max-w-[calc(100%-1.5rem)] flex-col items-start gap-2"
      >
        {analysisStatus === 'running' && (
          <div className="flex items-center gap-3 rounded-full border border-hairline bg-raised/90 px-4 py-1.5 text-xs text-ink backdrop-blur">
            <Loader2 aria-hidden className="h-3.5 w-3.5 animate-spin text-accent" />
            <span>{progress?.message ?? 'Analysing'}</span>
            <div
              role="progressbar"
              aria-label="Analysis progress"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(Math.min(100, progress?.percent ?? 0))}
              className="h-1 w-32 overflow-hidden rounded-full bg-inset"
            >
              <div
                className="h-full bg-accent transition-[width]"
                style={{ width: `${Math.min(100, progress?.percent ?? 0)}%` }}
              />
            </div>
          </div>
        )}
        {analysisStatus === 'error' && analysisError && (
          <div className="flex items-center gap-2 rounded-full border border-warning/50 bg-raised/90 px-4 py-1.5 text-xs text-ink backdrop-blur">
            <AlertTriangle aria-hidden className="h-3.5 w-3.5 shrink-0 text-warning" />
            <span>{analysisError}</span>
          </div>
        )}
        {sunOutage && (
          <div className="pointer-events-auto flex items-center gap-2 rounded-full border border-caution/50 bg-raised/90 px-3 py-1 text-xs text-ink backdrop-blur">
            <Sun aria-hidden className="h-3.5 w-3.5 shrink-0 text-caution" />
            <span>
              Sun outage {sunOutage.antennaId} with {sunOutage.missionId}: {Math.round(sunOutage.durationSec)}{' '}
              s at {sunOutage.separationDeg.toFixed(2)} deg
            </span>
          </div>
        )}
        {tracked && (
          <div className="pointer-events-auto flex items-center gap-2 rounded-full border border-accent/40 bg-raised/90 px-3 py-1 text-xs text-ink backdrop-blur">
            <Crosshair aria-hidden className="h-3.5 w-3.5 text-accent" />
            <span>Following {trackedMission?.shortName ?? tracked}</span>
            <button
              type="button"
              aria-label="Stop following"
              onClick={() => setTracked(null)}
              className="rounded-control text-ink-3 hover:text-ink"
            >
              <X aria-hidden className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
        {network !== 'full_catalogue' && <Chip
          tone={inContact > 0 ? 'live' : 'neutral'}
          className="pointer-events-auto bg-raised/90 backdrop-blur"
        >
          <LocateFixed aria-hidden className="h-3 w-3" />
          {network === 'rfims' ? '4 monitoring sites · coverage unverified' : `${inContact} antenna contacts`}
        </Chip>}
      </div>

      {/* Click-to-place: hint bar, dialog and engine wiring (waits for the lazily loaded globe engine). */}
      <PlacementLayer />
    </div>
  );
}
